import React, { useEffect, useMemo, useState } from 'react'
import {
  Column,
  SortingRule,
  useExpanded,
  useFlexLayout,
  useGlobalFilter,
  usePagination,
  useResizeColumns,
  useSortBy,
  useTable,
} from 'react-table'
import { EmailSent } from '../../@types'
import { useTranslation } from 'react-i18next'
import Header from '../../components/organisms/Table/atoms/Header'
import { emptyArray, sortByAlphabet } from '../../utils/array'
import { BlackParagraph } from '../../components/atoms/Paragraph'
import { highlight } from '../../utils/highlight'
import { useQuery, useQueryClient } from 'react-query'
import { emailResendMailFn, emailSentLogsFn } from '../../api'
import { LoadingPage } from '../../components/molecules/Loader'
import Table from '../../components/organisms/Table'
import { useUserContext } from '../../contexts/UserContext'
import { Dropdown } from '../../components/molecules/Dropdown'
import { IconButton } from '../../components/molecules/Button'
import { Icon } from '@iconify/react'
import { MenuItem, Popup } from '../../components/molecules/Popup'
import { MODALS } from '../../components/organisms/Modal'
import { LinkTo } from '../../components/atoms/Pointer'

const columns: Column<EmailSent>[] = [
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('page.emails.recipient')} />
    },
    accessor: 'recipient',
    sortType: (a, b) => sortByAlphabet(a.values.recipient, b.values.recipient),
    Cell: ({ value, state: { globalFilter } }) => (
      <BlackParagraph text={highlight(value, globalFilter.search)} size="12" />
    ),
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('page.emails.email')} />
    },
    accessor: 'email',
    sortType: (a, b) => sortByAlphabet(a.values.email, b.values.email),
    Cell: ({ value, state: { globalFilter } }) => (
      <BlackParagraph text={highlight(value, globalFilter.search)} size="12" />
    ),
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('page.emails.created_at')} />
    },
    accessor: 'created_at',
    sortType: (a, b) => sortByAlphabet(a.values.created_at, b.values.created_at),
    Cell: ({ value, state: { globalFilter } }) => (
      <BlackParagraph text={highlight(value, globalFilter.search)} size="12" />
    ),
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('page.emails.sent_at')} />
    },
    accessor: 'sent_at',
    sortType: (a, b) => sortByAlphabet(a.values.sent_at, b.values.sent_at),
    Cell: ({ value, state: { globalFilter } }) => (
      <BlackParagraph text={highlight(value, globalFilter.search)} size="12" />
    ),
  },
  {
    Header: () => null,
    accessor: 'id',
    id: 'edit',
    maxWidth: 40,
    disableSortBy: true,
    Cell: (cell) => {
      const [t] = useTranslation()
      const queryClient = useQueryClient()
      const { id } = cell.row.original

      return (
        <Dropdown
          button={
            <IconButton>
              <Icon icon="uil:ellipsis-v" />
            </IconButton>
          }
        >
          <Popup>
            <MenuItem
              value="change-fg"
              icon={<Icon icon="uil:sync" />}
              onClick={() =>
                emailResendMailFn(id)
                  .then(() => {
                    queryClient.invalidateQueries('email_sent_logs')
                  })
                  .catch((e) => {})
              }
            >
              {t('page.emails.resend')}
            </MenuItem>
            <LinkTo to={`/emails/${id}?modal=${MODALS.viewSentEmail}`}>
              <MenuItem value="change-fg" icon={<Icon icon="uil:edit" />} onClick={() => {}}>
                {t('common.edit')}
              </MenuItem>
            </LinkTo>
          </Popup>
        </Dropdown>
      )
    },
    width: 0,
  },
]

const EmailsSent = () => {
  const { state } = useUserContext()
  const { i_realm } = state?.realm

  const [pagination, setPagination] = useState(() => ({ pageIndex: 0, pageSize: 50 }))
  const [sortBy, setSortBy] = useState<SortingRule<EmailSent>[]>(() => [{ id: 'created_at', desc: true }])

  const { data: emailSent, isLoading } = useQuery(
    ['email_sent_logs', i_realm, pagination, sortBy[0]],
    emailSentLogsFn,
    {
      select: (data) => ({
        items: data.items,
        total: data.total,
      }),
    }
  )

  const pageCount = useMemo(
    () => Math.ceil(((emailSent?.total as number) || 0) / pagination.pageSize) || 0,
    [emailSent?.total, pagination.pageSize]
  )

  const tableProps = useTable(
    {
      columns,
      data: emailSent?.items ?? emptyArray,
      initialState: { pageSize: pagination.pageSize, globalFilter: { search: '' } },
      autoResetPage: false,
      disableSortRemove: true,
      manualPagination: true,
      manualSortBy: true,
      manualGlobalFilter: true,
      matablePropsnualGlobalFilter: true,
      pageCount: pageCount,
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useFlexLayout,
    useResizeColumns
  )

  useEffect(() => {
    const { pageIndex, pageSize, sortBy: _sortBy } = tableProps.state
    if (pageIndex !== pagination.pageIndex || pageSize !== pagination.pageSize) {
      setPagination({ pageIndex, pageSize })
    }

    if (_sortBy[0]) {
      const item = _sortBy[0]
      const currentItem = sortBy[0]
      if (!currentItem || item.id !== currentItem?.id || item?.desc !== currentItem?.desc) {
        setSortBy([item])
      }
    }
  }, [tableProps.state, pagination, sortBy])

  if (isLoading || !emailSent) {
    return <LoadingPage />
  }

  return <Table isLoading={isLoading} {...tableProps} />
}

export default EmailsSent
